/* ===================================================================
   usePlan() -- the ONE source of truth for the client's plan/subscription
   status (M2, Brad 2026-07-06).

   GET /api/v1/billing/plan -> { has_active_subscription, plan_display_name,
   status, tier }. Before this, the sidebar chip read me.tier (which can be a
   seeded "pro") while Billing read /billing/plan (which said "no plan"), so the
   two disagreed. Both now read THIS, and label off planLabel(), so the plan
   shown is consistent everywhere.

   Token-gated so it never 401s before login.
   =================================================================== */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type BillingPlan } from '../lib/api';
import { useApiToken } from './useApiToken';

export function usePlan(): UseQueryResult<BillingPlan, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: ['billing-plan'],
    queryFn: ({ signal }) => api.getPlan(signal),
    enabled: hasToken,
    staleTime: 30_000,
  });
}

/* One consistent plan label from the authoritative billing-plan payload.
   - active/trialing subscription -> "Pro - Active" / "Starter - Trial"
   - no active subscription       -> "No plan yet"
   - not loaded yet               -> "Rhody Reviews" (neutral, never a fake tier)
   Used by the sidebar plan chip AND the Billing panel chip so they agree. */
export function planLabel(plan: BillingPlan | undefined): string {
  if (!plan) return 'Rhody Reviews';
  if (!plan.has_active_subscription) return 'No plan yet';
  const name = plan.plan_display_name ?? 'Plan';
  const state = plan.status === 'trial' ? 'Trial' : 'Active';
  return `${name} - ${state}`;
}
